import { isDefined } from '@sgme/fp';
import { ReadonlyFieldProvider } from 'components/common-form/contexts/readonly/FieldMetadataContext';
import { DetailBuilder } from 'components/common-form/sidePanel/info/Details/DetailBuilder';
import { ListGroup, ListGroupItem } from 'components/shared/ListGroup';
import { QuoteRepriceLink } from 'components/shared/QuoteRepriceLink';
import { useIsInternalUser } from 'hooks/useIsInternalUser';
import type { IntlKey } from 'locales';
import { ReadonlyValueDefinition } from 'models/metadata/readonlyValue.definition';
import { FC, ReactNode } from 'react';
import {
  type QuoteDetailsData,
  type QuoteDetailsField,
  type QuoteTargetScreenMode,
} from 'state/quotes/details/details.model';
import type { QuoteWorkspaceType, WorkspaceType } from 'state/workspaces/model';

import { quoteDetailsFields } from '../QuoteDetails.model';
import { FieldValue } from './FieldValue';

interface QuoteDetailsProps {
  tabId: string;
  externalQuoteId: string;
  quoteId: string;
  workspaceType: WorkspaceType;
  data?: QuoteDetailsData;

  fieldsShouldBeReferenced?: boolean;

  quoteDetailsMetadata?: Readonly<Record<string, Readonly<ReadonlyValueDefinition>>>;
}

function getTargetWorkspace(quoteTargetScreenMode: QuoteTargetScreenMode | undefined): QuoteWorkspaceType | undefined {
  switch (quoteTargetScreenMode) {
    case undefined:
      return undefined;
    case 'Static':
      return 'static';
    case 'Dynamic':
      return 'dynamic';
    case 'Batch':
      return 'newBatch';
    case 'Addon':
      return 'postTrade';
    default:
      return 'static' as never;
  }
}

export const QuoteDetails: FC<QuoteDetailsProps> = ({
  externalQuoteId,
  quoteId,
  workspaceType,
  data,
  fieldsShouldBeReferenced = true,
  quoteDetailsMetadata,
}) => {
  const isInternal = useIsInternalUser();
  const targetWorkspaceType = getTargetWorkspace(data?.QuoteRepriceLinkTargetScreenMode);

  const item = (field: string, value: Readonly<unknown> | null, data: QuoteDetailsData) =>
    isDefined(value) && (
      <ListGroupItem
        key={field}
        labelKey={`quoteDetails.${field}` as IntlKey}
        defaultLabelMessage={field}
        className="ps-0 pe-3"
        e2e={field}
      >
        <FieldValue
          field={field}
          value={value}
          productType={data['ProductType'] ?? undefined}
          instrumentName={data['InstrumentName']}
          solvingMode={data['SolvingMode']}
        />
      </ListGroupItem>
    );

  let fields: ReactNode;

  if (data) {
    if (fieldsShouldBeReferenced) {
      if (isDefined(quoteDetailsMetadata)) {
        fields = (
          <ReadonlyFieldProvider data={data}>
            <DetailBuilder metadatas={quoteDetailsMetadata} />
          </ReadonlyFieldProvider>
        );
      } else {
        fields = quoteDetailsFields.map(field =>
          item(field, data[mapFieldForWorkspaceType(field, workspaceType)], data),
        );
      }
    } else {
      fields = Object.entries(data)
        .filter(([field]) => field !== 'SupplementDetails')
        .map(([field, value]) => item(field, value, data));
    }
  }

  return (
    <ListGroup className="mt-3" e2e="quote-details">
      {!data && (
        <ListGroupItem labelKey="quoteDetails.ExternalQuoteId" className="ps-0 pe-3" e2e="QuoteExternalId">
          <QuoteRepriceLink externalQuoteId={externalQuoteId} quoteId={quoteId} disabled />
        </ListGroupItem>
      )}

      {data && (
        <>
          <ListGroupItem labelKey="quoteDetails.ExternalQuoteId" className="ps-0 pe-3" e2e="QuoteExternalId">
            <QuoteRepriceLink
              externalQuoteId={externalQuoteId}
              quoteId={quoteId}
              disabled={targetWorkspaceType === workspaceType || workspaceType === 'dynamic'}
              targetWorkspace={targetWorkspaceType}
              canShare={isInternal}
            />
          </ListGroupItem>

          {fields}
        </>
      )}
    </ListGroup>
  );
};

function mapFieldForWorkspaceType(field: QuoteDetailsField, workspaceType: WorkspaceType): QuoteDetailsField {
  if (workspaceType !== 'static' && field === 'ProductSubtype') {
    return 'DocumentationTitle';
  }
  return field;
}

QuoteDetails.displayName = 'QuoteDetails';
